// Message Composer Component
// Following existing patterns from ProfileEditor

import React, { useState, useRef, useCallback } from 'react';
import { MessageComposerProps } from '../../../types/messaging';
import {
  MessageComposerContainer,
  MessageComposerForm,
  MessageInputContainer,
  MessageTextarea,
  MessageComposerActions,
  ComposerActionButton,
  AttachmentButton,
  EmojiButton,
  SendButton,
  AttachmentPreview,
  AttachmentPreviewInfo,
  AttachmentPreviewName,
  AttachmentPreviewSize,
  AttachmentPreviewRemove,
  EmojiPicker,
  EmojiGrid,
  EmojiItem,
  CharacterCount,
  MessageComposerError,
  UploadProgress,
  MessagingProgressBar as ProgressBar,
  MessagingProgressFill as ProgressFill,
  MessagingProgressText as ProgressText
} from '../../../styles/Messaging';

export const MessageComposer: React.FC<MessageComposerProps> = ({
  onSendMessage,
  onTyping,
  disabled = false,
  placeholder = "Type a message..."
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<any[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxMessageLength = 1000;
  const isOverLimit = message.length > maxMessageLength;

  // Common emojis for quick access
  const commonEmojis = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
    '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
    '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
    '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
    '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
    '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠',
    '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨',
    '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥',
    '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
    '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
    '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑'
  ];

  const handleMessageChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    // Send typing indicator
    if (onTyping) {
      onTyping(value.length > 0);
    }
  }, [onTyping]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [message, attachments]);

  const handleSend = useCallback(async () => {
    if (!message.trim() && attachments.length === 0) return;
    if (isOverLimit) return;
    if (disabled) return;

    try {
      setError(null);
      await onSendMessage(message.trim(), attachments);
      setMessage('');
      setAttachments([]);

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    }
  }, [message, attachments, isOverLimit, disabled, onSendMessage]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      const newAttachments = validFiles.map(file => ({
        id: Date.now() + Math.random(),
        type: file.type.startsWith('image/') ? 'image' :
          file.type.startsWith('video/') ? 'video' :
            file.type.startsWith('audio/') ? 'audio' : 'file',
        file,
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file)
      }));

      setAttachments(prev => [...prev, ...newAttachments]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleRemoveAttachment = useCallback((attachmentId: string) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === attachmentId);
      if (attachment?.url) {
        URL.revokeObjectURL(attachment.url);
      }
      return prev.filter(a => a.id !== attachmentId);
    });
  }, []);

  const handleEmojiSelect = useCallback((emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);

    // Focus back to textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <MessageComposerContainer>
      {attachments.length > 0 && (
        <AttachmentPreview>
          {attachments.map((attachment) => (
            <div key={attachment.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {attachment.type === 'image' && (
                <img
                  src={attachment.url}
                  alt={attachment.name}
                  style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                />
              )}
              <AttachmentPreviewInfo>
                <AttachmentPreviewName>{attachment.name}</AttachmentPreviewName>
                <AttachmentPreviewSize>{formatFileSize(attachment.size)}</AttachmentPreviewSize>
              </AttachmentPreviewInfo>
              <AttachmentPreviewRemove onClick={() => handleRemoveAttachment(attachment.id)}>
                ✕
              </AttachmentPreviewRemove>
            </div>
          ))}
        </AttachmentPreview>
      )}

      {isUploading && (
        <UploadProgress>
          <ProgressBar>
            <ProgressFill $progress={uploadProgress} />
          </ProgressBar>
          <ProgressText>{uploadProgress}%</ProgressText>
        </UploadProgress>
      )}

      <MessageComposerForm onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
        <MessageInputContainer>
          <MessageTextarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            style={{
              minHeight: '40px',
              maxHeight: '120px',
              resize: 'none'
            }}
          />

          <MessageComposerActions>
            <EmojiButton
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={disabled}
              title="Add emoji"
            >
              😊
            </EmojiButton>
          </MessageComposerActions>
        </MessageInputContainer>

        <SendButton
          type="submit"
          $disabled={!message.trim() && attachments.length === 0 || isOverLimit || disabled}
        >
          Send
        </SendButton>
      </MessageComposerForm>

      {showEmojiPicker && (
        <EmojiPicker>
          <EmojiGrid>
            {commonEmojis.map((emoji, index) => (
              <EmojiItem
                key={index}
                type="button"
                onClick={() => handleEmojiSelect(emoji)}
              >
                {emoji}
              </EmojiItem>
            ))}
          </EmojiGrid>
        </EmojiPicker>
      )}

      <CharacterCount $isOverLimit={isOverLimit}>
        {message.length}/{maxMessageLength}
      </CharacterCount>

      {error && (
        <MessageComposerError>
          {error}
        </MessageComposerError>
      )}
    </MessageComposerContainer>
  );
};
